/**
 * AudioMixTester Component
 *
 * Provides a "Test" button for testing audio mixing specifications
 * in ShowEdit without risking production stories.
 *
 * Creates a temporary mixed audio using demo TTS + show audio paths,
 * following the PromptDebugger pattern (no callback, direct queue subscription).
 *
 * @module AudioMixTester
 * @version 1.0 - Issue #417
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Alert,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { PlayCircle } from 'lucide-react';
import { useNotify } from 'react-admin';
import type { MixingTrack } from '@musedrops/shared';
import {
  getMediaUrl,
  getMediaUrlByPath,
  logger,
  MediaConfig,
} from '@musedrops/shared';
import { useQueueJob } from '../../../hooks/useQueueJob';
import { enqueueJob } from '../../../data/process';
import { supabase } from '../../../services/supabase';

/**
 * Placeholder data interface for audio path substitution
 */
interface PlaceholderData {
  'voice-path': string;
  'intro-music-path': string;
  'outro-music-path'?: string;
  'music-path'?: string;
  'theme-music-path'?: string;
  'announce-path'?: string;
  'signature-path'?: string;
}

/**
 * Substitute placeholders in mixing spec tracks with actual audio URLs
 * Mirrors the logic from gen-story-mixed-audio Edge Function
 */
function substitutePlaceholders(
  template: MixingTrack[],
  data: PlaceholderData,
  showSlug: string
): MixingTrack[] {
  const result: MixingTrack[] = [];

  for (const track of template) {
    // Deep clone the track
    const newTrack: MixingTrack = JSON.parse(JSON.stringify(track));

    // Substitute in source - convert filenames to full URLs
    if (newTrack.source) {
      for (const [key, value] of Object.entries(data)) {
        if (value) {
          // Only process defined values
          const placeholder = `{{${key}}}`;
          if (newTrack.source.includes(placeholder)) {
            // Determine the folder based on the key
            // Show music files are in 'shows' folder, voice/demo files might be elsewhere
            let url: string;
            if (value.includes('/')) {
              // Full path (e.g., from config like signature-path or temp demo files)
              url = getMediaUrlByPath(value);
            } else {
              // New: value is filename only, construct path from folder + slug + filename
              // Use Date.now() for cache-busting since tests are run repeatedly
              url = `${getMediaUrl('shows', showSlug, value)}?t=${Date.now()}`;
            }
            newTrack.source = url;
          }
        }
      }
    }

    result.push(newTrack);
  }

  return result;
}

/**
 * Filter signature track for internal audio variant.
 * Removes tracks with id='signature' or source containing 'signature'
 *
 * @param tracks - Input tracks
 * @returns Tracks without signature track
 */
function filterSignatureTrack(tracks: MixingTrack[]): MixingTrack[] {
  return tracks.filter(track => {
    if (track.id === 'signature') return false;
    if (track.source && track.source.includes('signature')) return false;
    return true;
  });
}

/**
 * Remove tracks with missing sources and perform cascading removal
 * Mirrors the logic from gen-story-mixed-audio Edge Function
 */
function removeTracksWithMissingSources(tracks: MixingTrack[]): MixingTrack[] {
  const removedIds = new Set<string>();

  // Find tracks with missing sources (empty or unsubstituted placeholders)
  for (const track of tracks) {
    if (
      !track.source ||
      track.source.trim() === '' ||
      track.source.includes('{{')
    ) {
      if (track.id) {
        removedIds.add(track.id);
      }
    }
  }

  // If no tracks were removed, return as-is
  if (removedIds.size === 0) {
    return tracks;
  }

  // Cascade: find tracks that depend on removed tracks
  let changed = true;
  while (changed) {
    changed = false;
    for (const track of tracks) {
      // Skip already removed tracks
      if (!track.id || !removedIds.has(track.id)) {
        // Check if this track's offset references a removed track
        if (typeof track.offset === 'string') {
          const parts = track.offset.trim().split(/\s+/);
          const refTrackId = parts[0];
          if (refTrackId && removedIds.has(refTrackId)) {
            if (track.id) {
              removedIds.add(track.id);
            }
            changed = true;
          }
        }
      }
    }
  }

  // Filter out removed tracks
  return tracks.filter(track => {
    if (
      !track.source ||
      track.source.trim() === '' ||
      track.source.includes('{{')
    ) {
      return false;
    }
    if (track.id && removedIds.has(track.id)) {
      return false;
    }
    if (typeof track.offset === 'string') {
      const parts = track.offset.trim().split(/\s+/);
      const refTrackId = parts[0];
      if (refTrackId && removedIds.has(refTrackId)) {
        return false;
      }
    }
    return true;
  });
}

interface AudioMixTesterProps {
  showSlug: string;
  showId: string;
  showLang?: string;
  introMusicUpdatedAt: string | null;
  outroMusicUpdatedAt: string | null;
  musicUpdatedAt: string | null;
  themeMusicUpdatedAt?: string | null;
  announceUpdatedAt?: string | null;
  mixingSpec: MixingTrack[] | null;
  disabled?: boolean;
}

export const AudioMixTester: React.FC<AudioMixTesterProps> = ({
  showSlug,
  showId,
  showLang = 'en',
  introMusicUpdatedAt,
  outroMusicUpdatedAt,
  musicUpdatedAt,
  themeMusicUpdatedAt,
  announceUpdatedAt,
  mixingSpec,
  disabled = false,
}) => {
  const notify = useNotify();
  const [testAudioUrl, setTestAudioUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeSignature, setIncludeSignature] = useState(true);

  // Derive conventional filenames from MediaConfig
  const introMusicFile = introMusicUpdatedAt
    ? MediaConfig.show.introMusic
    : null;
  const outroMusicFile = outroMusicUpdatedAt
    ? MediaConfig.show.outroMusic
    : null;
  const musicFile = musicUpdatedAt ? MediaConfig.show.backgroundMusic : null;
  const themeMusicFile = themeMusicUpdatedAt
    ? MediaConfig.show.themeMusic
    : null;
  const announceFile = announceUpdatedAt
    ? MediaConfig.show.showAnnounce.replace('{lang}', showLang)
    : null;

  // Queue job hook for watching test mixing completion
  const { watchJob } = useQueueJob({
    timeout: 5 * 60 * 1000, // 5 minutes
    onComplete: output => {
      logger.info('✅ [AUDIO-MIX-TEST] Job completed', { output });

      // Extract audio path from output (stored in temp/ folder)
      const audioPath =
        output?.audioPath || output?.path || output?.mixedAudioFile;
      if (audioPath) {
        // Use getMediaUrlByPath since we have the full storage path (temp/test-audio-mix.mp3)
        const audioUrl = getMediaUrlByPath(audioPath as string);
        // Add cache-busting timestamp to prevent stale cache
        const timestamp = Date.now();
        setTestAudioUrl(`${audioUrl}?t=${timestamp}`);
        notify('Test audio generated successfully', { type: 'success' });
      } else {
        logger.error('❌ [AUDIO-MIX-TEST] No audio path in output', { output });
        setError('Test completed but no audio URL found');
        notify('Test completed but no audio URL found', { type: 'error' });
      }
      setIsLoading(false);
    },
    onError: err => {
      logger.error('❌ [AUDIO-MIX-TEST] Job failed', { error: err });
      setError(err);
      notify(`Test mixing failed: ${err}`, { type: 'error' });
      setIsLoading(false);
    },
    onTimeout: () => {
      logger.error('❌ [AUDIO-MIX-TEST] Job timed out');
      setError('Test mixing timed out after 5 minutes');
      notify('Test mixing timed out after 5 minutes', { type: 'error' });
      setIsLoading(false);
    },
  });

  /**
   * Load demo TTS path from config table
   */
  const loadDemoTtsPath = async (): Promise<string> => {
    const { data, error: configError } = await supabase
      .from('config')
      .select('value')
      .eq('key', 'demo_tts_path')
      .eq('category', 'audio')
      .maybeSingle();

    if (configError) {
      throw new Error(`Failed to load demo TTS config: ${configError.message}`);
    }

    if (!data || !data.value) {
      throw new Error(
        'Demo TTS path not configured. Please add demo_tts_path in Settings > Audio Mix Template'
      );
    }

    return data.value as string;
  };

  /**
   * Load signature audio path from config table (optional)
   */
  const loadSignaturePath = async (): Promise<string | null> => {
    const { data, error: configError } = await supabase
      .from('config')
      .select('value')
      .eq('key', 'signature_audio_path')
      .eq('category', 'audio')
      .maybeSingle();

    if (configError) {
      logger.error('Failed to load signature audio path', {
        error: configError,
      });
      return null;
    }

    return (data?.value as string) || null;
  };

  /**
   * Load default mixing spec from config table
   */
  const loadDefaultMixingSpec = async (): Promise<MixingTrack[]> => {
    const { data, error: configError } = await supabase
      .from('config')
      .select('value')
      .eq('key', 'default_mixing_spec')
      .eq('category', 'audio')
      .maybeSingle();

    if (configError) {
      throw new Error(
        `Failed to load default mixing spec: ${configError.message}`
      );
    }

    if (!data || !data.value) {
      throw new Error('Default mixing spec not configured in Settings');
    }

    // Parse the JSON value
    const parsed =
      typeof data.value === 'string' ? JSON.parse(data.value) : data.value;

    if (!parsed.tracks || !Array.isArray(parsed.tracks)) {
      throw new Error(
        'Invalid default mixing spec format: missing tracks array'
      );
    }

    return parsed.tracks as MixingTrack[];
  };

  /**
   * Create test mixing job
   */
  const handleTest = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setTestAudioUrl(null); // Clear previous test audio

      // Load required and optional audio paths from config
      const demoTtsPath = await loadDemoTtsPath();
      const signaturePath = await loadSignaturePath();

      // Get mixing spec template - use custom spec or load default
      let template: MixingTrack[];
      if (mixingSpec && mixingSpec.length > 0) {
        template = mixingSpec;
      } else {
        template = await loadDefaultMixingSpec();
      }

      logger.info('🎙️ [AUDIO-MIX-TEST] Creating test mixing job', {
        showSlug,
        showId,
        demoTtsPath,
        introMusicFile,
        templateTrackCount: template.length,
      });

      // Build placeholders object for substitution
      const placeholders: PlaceholderData = {
        'voice-path': demoTtsPath,
        'intro-music-path': introMusicFile,
      };

      // Add optional paths only if they exist
      if (outroMusicFile) {
        placeholders['outro-music-path'] = outroMusicFile;
      }
      if (musicFile) {
        placeholders['music-path'] = musicFile;
      }
      if (themeMusicFile) {
        placeholders['theme-music-path'] = themeMusicFile;
      }
      if (announceFile) {
        placeholders['announce-path'] = announceFile;
      }
      if (signaturePath) {
        placeholders['signature-path'] = signaturePath;
      }

      // Step 1: Substitute placeholders in template
      const tracksWithPlaceholdersReplaced = substitutePlaceholders(
        template,
        placeholders,
        showSlug
      );

      // Step 2: Filter signature track for internal variant
      const tracksAfterVariantFilter = includeSignature
        ? tracksWithPlaceholdersReplaced
        : filterSignatureTrack(tracksWithPlaceholdersReplaced);

      // Step 3: Remove tracks with missing sources (cascading removal)
      const tracks = removeTracksWithMissingSources(tracksAfterVariantFilter);

      if (tracks.length === 0) {
        throw new Error(
          'No valid tracks after placeholder substitution and cascading removal'
        );
      }

      logger.info('🎙️ [AUDIO-MIX-TEST] Processed tracks', {
        originalCount: template.length,
        afterSubstitution: tracksWithPlaceholdersReplaced.length,
        afterVariantFilter: tracksAfterVariantFilter.length,
        finalCount: tracks.length,
        variant: includeSignature ? 'external' : 'internal',
      });

      // Build payload for Cloud Run - MUST match expected format: {tracks, storagePath}
      const payload = {
        tracks, // Already processed with placeholders substituted and cascading removal done
        storagePath: 'temp/test-audio-mix.mp3', // Reuses same file (upsert enabled for temp/)
      };

      // Create queue job with no callback (PromptDebugger pattern)
      const job = await enqueueJob({
        entityType: 'show',
        entityId: showId,
        process: 'gen-story-mixed-audio', // Issue #847: canonical name (was 'audio-mixing')
        trigger: 'manual', // UI-initiated jobs (derives priority: true)
        callbackFunction: null, // No callback - we'll watch directly
        payload,
      });

      logger.info('🎙️ [AUDIO-MIX-TEST] Job created, watching', {
        jobId: job.id,
      });

      // Watch the job via real-time subscription
      watchJob(job.id);
    } catch (err) {
      logger.error('❌ [AUDIO-MIX-TEST] Failed to create job', { error: err });
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create test mixing job';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
      setIsLoading(false);
    }
  }, [
    showSlug,
    showId,
    introMusicFile, // Derived from introMusicUpdatedAt
    outroMusicFile, // Derived from outroMusicUpdatedAt
    musicFile, // Derived from musicUpdatedAt
    themeMusicFile,
    announceFile,
    mixingSpec,
    includeSignature,
    notify,
    watchJob,
  ]);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ mb: 1 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleTest}
          disabled={disabled || isLoading}
          startIcon={<PlayCircle size={16} />}
          size="small"
        >
          {isLoading ? 'Testing...' : 'Test Mix'}
        </Button>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={includeSignature}
            onChange={e => setIncludeSignature(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="body2">
            Include signature/logo (external audio for RSS)
          </Typography>
        }
        sx={{ ml: 0 }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {testAudioUrl && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Test Audio Mix:
          </Typography>
          <audio
            controls
            autoPlay
            src={testAudioUrl}
            style={{ width: '100%', maxWidth: '500px' }}
          >
            <track kind="captions" src="" label="Audio captions" />
            Your browser does not support the audio element.
          </audio>
        </Box>
      )}
    </Box>
  );
};
